import { z } from 'zod';

import {
  contestationTypeWireLabel,
  statusNumber,
  statusWireLabel,
} from './pix-med.constants';

// Contrato de fio do Pix Med Admin (U27), lido do legado (docs/discovery/modulos/pix-med.md §4):
// `services/pixMedAdmin/pixMedAdmin.types.ts`, o serviço e o que a tela lê.

const text = z.string().nullish();

/**
 * `IPixMedAdminSearchResponse` (`pixMedAdmin.types.ts:24-41`) + `DefaultRecordDetails`. Solto (loose): a
 * linha inteira volta no corpo do PUT, inclusive o que a API manda além do tipo (`bankName`, que a lista lê).
 */
export const pixMedContestationSchema = z.looseObject({
  id: text,
  infractionReportId: text,
  registrationNumber: text,
  bankCode: z.union([z.string(), z.number()]).nullish(),
  bankName: text,
  agencyCode: text,
  accountNumber: text,
  transactionId: text,
  transactionDate: text,
  transactionTime: text,
  status: text,
  recipientName: text,
  contestationType: z.union([z.string(), z.number()]).nullish(),
  /** Centavos (`formatCurrencyInCents`, `PixMedAdminFormList.tsx:152`). */
  operationAmount: z.number().nullish(),
  recoveredAmount: z.number().nullish(),
  createdAt: text,
  lastUpdatedAt: text,
});
export type PixMedContestationWire = z.infer<typeof pixMedContestationSchema>;

/** A linha como a tela a usa: o `id` já é o índice sintético da página (`PixMedAdminContext.tsx:53-58`). */
export type PixMedContestation = PixMedContestationWire & { readonly id: string };

/** `IPixMedAdminSearchResponseFull` (`pixMedAdmin.types.ts:17-22`): `page` 1-based. */
export const pixMedContestationPageSchema = z.looseObject({
  data: z.array(pixMedContestationSchema),
  page: z.number(),
  totalPages: z.number(),
  totalItems: z.number(),
});
export type PixMedContestationPage = z.infer<typeof pixMedContestationPageSchema>;

/**
 * Filtros aplicados, legíveis (URL e rascunho): documento cru, data civil, valores técnicos. `bankName` e
 * `counterpartyBankIspb` acompanham o banco escolhido, como o autocomplete do legado grava (§3).
 */
export interface PixMedFilters {
  readonly contestationType?: string | undefined;
  readonly recipientName?: string | undefined;
  readonly registrationNumber?: string | undefined;
  readonly bankCode?: string | undefined;
  readonly bankName?: string | undefined;
  readonly counterpartyBankIspb?: string | undefined;
  readonly agencyCode?: string | undefined;
  readonly accountNumber?: string | undefined;
  readonly transactionId?: string | undefined;
  readonly startDate?: string | undefined;
  readonly status?: string | undefined;
  /**
   * Os seletores que a pessoa mexeu, na ordem do primeiro toque (`contestationType`, `bank`, `status`, por
   * vírgula). É o que decide a ordem e o "limpo" dos auxiliares no fio (Q7/R3, ver `pixMedSearchQuery`).
   * Ausente (agente, link antigo): os seletores com valor, na ordem da tela.
   */
  readonly auxiliares?: string | undefined;
}

/** Os três seletores que gravam campos auxiliares no fio (`SelectFormField`, `AutocompleteField` do legado). */
export const PIX_MED_AUX_GROUPS = ['contestationType', 'bank', 'status'] as const;
export type PixMedAuxGroup = (typeof PIX_MED_AUX_GROUPS)[number];

/** `auxiliares` lido da URL: só grupos conhecidos, sem repetir, na ordem dada. */
export function parseAuxiliaries(value: string | undefined): PixMedAuxGroup[] | undefined {
  if (value === undefined) return undefined;
  const out: PixMedAuxGroup[] = [];
  for (const part of value.split(',')) {
    const group = PIX_MED_AUX_GROUPS.find((candidate) => candidate === part);
    if (group && !out.includes(group)) out.push(group);
  }
  return out;
}

export const PIX_MED_FILTER_KEYS = [
  'contestationType',
  'recipientName',
  'registrationNumber',
  'bankCode',
  'bankName',
  'counterpartyBankIspb',
  'agencyCode',
  'accountNumber',
  'transactionId',
  'startDate',
  'status',
] as const satisfies readonly (keyof PixMedFilters)[];

/** O que a URL guarda como consulta aplicada: os filtros e a ordem dos seletores mexidos. */
export const PIX_MED_APPLIED_KEYS = [...PIX_MED_FILTER_KEYS, 'auxiliares'] as const satisfies readonly (keyof PixMedFilters)[];

export interface PixMedSearchParams {
  readonly filters: PixMedFilters;
  /** Página a partir de 0 (a tela); vai 1-based no fio. */
  readonly page: number;
  readonly size: number;
  /** `FEATURE_FLAGS.CNPJ_ALPHANUMERIC`: o `cleanDocumentValue` do legado mantém letras. */
  readonly alphanumeric: boolean;
}

const present = (value: string | undefined): value is string => value !== undefined && value !== '';
const orNull = (value: string | undefined) => (present(value) ? value : null);

/** `cleanDocumentValue` (`helpers/validations/formFieldValidator.ts:138-142`). */
export function cleanDocument(value: string, alphanumeric: boolean): string {
  return alphanumeric ? value.replace(/[^A-Za-z0-9]/g, '') : value.replace(/\D/g, '');
}

/**
 * Query de `GET /PixMED/Contestations` na ordem do legado (inventário §4.1): os nove campos do formulário
 * sempre (vazio vai `null`, que o `qs` serializa como `chave=`), os auxiliares que os seletores gravam, a página
 * 1-based, `pageSize`, `isFilter` e, por último, `endDate` **igual a `startDate`** (`pixMedAdmin.ts:12-13`).
 *
 * Auxiliares (R3/Q7), como o react-hook-form do legado os deixa nos valores do formulário:
 * - a chave nasce no primeiro toque do seletor e fica no lugar (`getValues()` preserva a ordem de inserção),
 *   então a ordem é a do primeiro toque de cada seletor, depois dos nove campos padrão;
 * - select escolhido: `<campo>Display` com o rótulo; limpo pelo "x": `<campo>Display=` (`SelectFormField.tsx:121-124`);
 * - banco escolhido: `bankName` e `counterpartyBankIspb` (`AutocompleteField.tsx:40-53`, `PixMedAdminForm.tsx:66-75`);
 *   limpo: `bankCode` **some** (`undefined`, o `qs` descarta) e vão `bankName=&counterpartyBankIspb=`
 *   (`AutocompleteField.tsx:33-35`, `PixMedAdminForm.tsx:67-69`). O `bankCodeDiscriminator` é `undefined` nos
 *   dois casos (a opção de banco não tem discriminador, `contexts/bankList.tsx:40-44`) e o `qs` descarta.
 */
export function pixMedSearchQuery(params: PixMedSearchParams): Record<string, unknown> {
  const { filters } = params;
  const day = present(filters.startDate) ? filters.startDate.split('T')[0] : null;
  const document = present(filters.registrationNumber) ? cleanDocument(filters.registrationNumber, params.alphanumeric) : null;
  const touched = auxiliaryOrder(filters);
  const bankCleared = touched.includes('bank') && !present(filters.bankCode);
  const query: Record<string, unknown> = {
    contestationType: present(filters.contestationType) ? Number(filters.contestationType) : null,
    recipientName: orNull(filters.recipientName),
    registrationNumber: document,
    bankCode: bankCleared ? undefined : orNull(filters.bankCode),
    agencyCode: orNull(filters.agencyCode),
    accountNumber: orNull(filters.accountNumber),
    transactionId: orNull(filters.transactionId),
    startDate: day,
    status: orNull(filters.status),
  };
  for (const group of touched) {
    if (group === 'contestationType') {
      query['contestationTypeDisplay'] = present(filters.contestationType) ? (contestationTypeWireLabel(filters.contestationType) ?? null) : null;
    } else if (group === 'status') {
      query['statusDisplay'] = present(filters.status) ? (statusWireLabel(filters.status) ?? null) : null;
    } else if (bankCleared) {
      query['bankName'] = '';
      query['counterpartyBankIspb'] = '';
    } else {
      query['bankName'] = filters.bankName ?? '';
      query['counterpartyBankIspb'] = filters.counterpartyBankIspb ?? '';
    }
  }
  query['page'] = params.page + 1;
  query['pageSize'] = params.size;
  query['isFilter'] = true;
  query['endDate'] = day;
  return query;
}

const groupHasValue = (filters: PixMedFilters, group: PixMedAuxGroup) =>
  present(group === 'bank' ? filters.bankCode : filters[group]);

/**
 * Ordem dos seletores mexidos: a de `auxiliares` e, depois, qualquer seletor com valor que não esteja lá (no
 * legado, ter valor implica ter sido tocado), na ordem da tela.
 */
export function auxiliaryOrder(filters: PixMedFilters): PixMedAuxGroup[] {
  const order = parseAuxiliaries(filters.auxiliares) ?? [];
  for (const group of PIX_MED_AUX_GROUPS) if (!order.includes(group) && groupHasValue(filters, group)) order.push(group);
  return order;
}

/** O que o painel devolve: o status escolhido, a observação e se cada um foi tocado (RHF do legado). */
export interface PixMedStatusChange {
  readonly status: string;
  readonly observation?: string | undefined;
  /** A pessoa escolheu uma opção: o `SelectFormField` do legado grava `statusDisplay`. */
  readonly statusChosen: boolean;
  /** A pessoa mexeu na observação: sem isso ela fica `undefined` e some do JSON. */
  readonly observationTouched: boolean;
}

/**
 * Corpo do `PUT /PixMED/Contestations/Status` (inventário §4.2): a linha inteira do formulário do drawer
 * (`getValues()` cru, `formContext.tsx:171-181`), com o `id` sintético, o `status` escolhido, `observation` e
 * `statusDisplay` quando existem e `newStatus` numérico (`pixMedAdmin.ts:32-39`). Status fora da lista não
 * manda `newStatus`.
 */
export function pixMedStatusBody(row: PixMedContestation, change: PixMedStatusChange): Record<string, unknown> {
  const body: Record<string, unknown> = { ...row, status: change.status };
  if (change.observationTouched) body['observation'] = change.observation ?? '';
  if (change.statusChosen) body['statusDisplay'] = statusWireLabel(change.status) ?? null;
  const number = statusNumber(change.status);
  if (number !== undefined) body['newStatus'] = number;
  return body;
}
